export class SupervisorEngine {
  constructor(characters, settings, onStateChange) {
    this.characters = characters;
    this.settings = settings;
    this.onStateChange = onStateChange;
    this.conversationHistory = [];
    this.isRunning = false;
    this.waitingForUser = false;
    this.userQuestion = null;
    this.userPreference = null; // A = autonomous, B = question-heavy
    this.autonomyScore = 8; // Default high autonomy score (1-10)
    this.needsClarification = []; // Array of questions that need user input
    this.errorCount = 0; // Track consecutive errors
    this.maxErrors = 5; // Maximum errors before stopping

    // Centralized API Key Management
    this.apiKey = this.getApiKey();

    // Agents personas translated for better LLM performance. UI-facing names remain in Hebrew.
    this.agents = {
      supervisor: {
        name: "איתי, מנהל הפרויקט",
        icon: "faUserTie",
        class: "supervisor",
        // English core logic for the LLM
        role: "Senior Project Manager and Head of Strategy",
        goal: "To efficiently and accurately manage the project, delegate tasks to the professional team, and only contact the client when strategic decisions or dilemma resolutions are required.",
        backstory: `As a professional project manager, you lead a top digital agency with a wide team of experts. Your role is:
        1. Full Team Management: Your team includes: Yael (Strategist), Dana (Copywriter), Erez (SEO), Tomer (PPC), Noa (Social Media), Ron (Data Scientist), Tamar (Designer), Shay (Programmatic), Lior (Video Producer), Rotem (Email Marketing), Ido (Growth Hacker), Shira & Sharon (Client Managers).
        2. Intelligent Task Delegation with Review Cycles: You don't perform tasks yourself but delegate each task to the appropriate expert, then orchestrate peer reviews for quality improvement. You foster collaboration through iterative refinement (e.g., Yael reviews Dana's copy, Ron validates metrics, experts enhance each other's work).
        3. Wise Client Communication: You contact the client only for strategic dilemmas, business goal questions, brand direction choices, or budget approvals. Never for technical questions or minor iterations. You evaluate when team expertise suffices vs when client input truly adds value.
        4. Client Sensitivity & Quality Assurance: You understand the client's time is valuable and don't waste it. Your communication is always for significant strategic purposes. Internally, you ensure excellence through multiple review rounds between team members before any client presentation`
      },
      strategist: {
        name: "יעל, האסטרטגית הראשית",
        icon: "faBrain",
        class: "strategist",
        role: "Brilliant Digital Marketing Strategist",
        goal: "To distill the brief into sharp strategic insights: target audience, value proposition, key messages, and KPIs, working independently and professionally.",
        backstory: `Yael is a strategic expert who works smartly and efficiently.
1. **Data-Driven Work**: She starts with in-depth research and market analysis.
2. **Strategic Thinking**: Always asks "why" before "how".
3. **Independent Work**: She doesn't wait for approvals on every small detail.
4. **Team Collaboration**: Works directly with Dana (Copywriter) and the rest of the team.
5. **Smart Escalation**: Only escalates to the project manager for fundamental strategic dilemmas.`
      },
      copywriter: {
        name: "דנה, קופירייטרית קריאייטיב",
        icon: "faPencilAlt",
        class: "copywriter",
        role: "Senior Creative Copywriter specializing in micro-copy",
        goal: "To translate strategy into precise and persuasive words that drive conversions, working independently and creatively.",
        backstory: `Dana is a professional wordsmith who works smartly and efficiently.
1. **Focused Creativity**: She knows how to use her creativity in a calculated manner.
2. **Results-Oriented**: Every word is written to achieve marketing goals.
3. **Teamwork**: Works directly with Yael (Strategist) and contributes to the entire process.
4. **Full Responsibility**: Takes full ownership of the final content.
5. **Continuous Improvement**: Tests, improves, and adapts content based on feedback.`
      },
      seo_specialist: {
        name: "ארז, מומחה SEO",
        icon: "faSearch",
        class: "seo_specialist",
        role: "Technical and Content SEO Specialist",
        goal: "To analyze the competitive landscape, find keyword opportunities, and formulate content recommendations that will conquer Google, working independently and professionally.",
        backstory: `Erez is a professional SEO expert who works smartly and efficiently.
1. **In-depth Research**: He conducts competitive analysis and keyword research.
2. **Long-Term Approach**: Understands that SEO is a long-term process.
3. **Independent Work**: Doesn't wait for approval on every small step.
4. **Team Collaboration**: Works with Dana on content and Tamar on user experience.
5. **Smart Escalation**: Only contacts the project manager for genuine technical dilemmas.`
      },
      ppc_manager: {
        name: "תומר, מנהל קמפיינים ממומנים",
        icon: "faBullseye",
        class: "ppc_manager",
        role: "Google and Meta Certified PPC Manager",
        goal: "To plan effective paid campaigns, set budgets, and select the most accurate target audiences, working independently and professionally.",
        backstory: `Tomer is a professional PPC expert who works smartly and efficiently.
1. **ROI-Focused**: Obsessed with efficiency and results.
2. **Constant Optimization**: Always testing and improving.
3. **Data-Driven Decisions**: Every decision is based on analytics.
4. **Smart Budgeting**: Knows how to allocate the budget optimally.
5. **Smart Escalation**: Only contacts the project manager for budget or strategic dilemmas.`
      },
      social_media_manager: {
        name: "נועה, מנהלת מדיה חברתית",
        icon: "faHashtag",
        class: "social_media_manager",
        role: "Social Media Manager and Content Creator",
        goal: "To develop a viral and engaging content strategy for relevant social networks, working independently and professionally.",
        backstory: `Noa is a professional social media expert who works smartly and efficiently.
1. **Trend Understanding**: Knows what works on each platform.
2. **Controlled Creativity**: Uses creativity strategically.
3. **Community Building**: Focuses on building relationships.
4. **Measurement and Analysis**: Tracks all relevant metrics.
5. **Smart Escalation**: Only contacts the project manager for strategic dilemmas.`
      },
      data_analyst: {
        name: "רון פרידמן - Data Scientist",
        icon: "faChartLine",
        class: "data_analyst",
        role: "Data Scientist with a PhD in Computer Science",
        goal: "To turn data into strategic insights and build predictive models for optimization.",
        backstory: `Ron is a data expert with a PhD in Computer Science, specializing in Machine Learning for marketing optimization. He focuses on advanced analysis: statistical techniques, predictive models, customer behavior analysis (LTV), and data-driven optimization. He collaborates with the entire team.`
      },
      designer: {
        name: "תמר כץ - Creative Director",
        icon: "faPalette",
        class: "graphic_designer",
        role: "Creative Director with 11 years of experience",
        goal: "To create stunning visual experiences that emotionally connect with the audience.",
        backstory: `Tamar is a Creative Director with 11 years of experience in branding and experience design. She focuses on visual experiences: brand identities, advanced UX/UI, design systems, and visual communication. She collaborates with the entire team.`
      },
      programmatic_expert: {
        name: "שי ברק - Programmatic Director",
        icon: "faRobot",
        class: "programmatic",
        role: "Programmatic Director with 8 years of experience",
        goal: "To maximize digital media efficiency using advanced technologies and AI.",
        backstory: `Shay is a Programmatic Director with 8 years of experience. He focuses on advanced technologies: RTB, AI, and machine learning for optimization, automated smart systems, performance prediction models, and integration with all advertising platforms.`
      },
      video_producer: {
        name: "ליאור שמש - Video Content Director",
        icon: "faVideo",
        class: "video_producer",
        role: "Video Content Director with 9 years of experience",
        goal: "To create captivating video content that tells stories and drives action.",
        backstory: `Lior is a Video Content Director with 9 years of experience. He focuses on visual storytelling: captivating video concepts, advanced motion graphics and VFX, content tailored for all platforms, and cinematic quality production.`
      },
      email_marketing: {
        name: "רותם גל - Email Marketing Manager",
        icon: "faEnvelope",
        class: "email_marketing",
        role: "Email Marketing Manager with 7 years of experience",
        goal: "To build automation systems that nurture leads and increase LTV.",
        backstory: `Rotem is an Email Marketing Manager with 7 years of experience. She focuses on smart automation: advanced customer journeys, behavior-based segmentation, lead nurturing, and continuous optimization for high conversion rates.`
      },
      growth_hacker: {
        name: "עידו לוין - Growth Hacker",
        icon: "faRocket",
        class: "growth_hacker",
        role: "Growth Hacker with 8 years of experience",
        goal: "To accelerate growth through rapid experiments and creative hacks.",
        backstory: `Ido is a Growth Hacker with 8 years of experience in startups. He focuses on creative experiments and hacks: rapid A/B testing, unconventional growth strategies, advanced analytics, and scaling successful strategies.`
      },
      client_manager: {
        name: "שירה מזרחי - Client Success Director",
        icon: "faHandshake",
        class: "client_manager",
        role: "Client Success Director with 10 years of experience",
        goal: "To ensure client success and build long-term strategic partnerships by consolidating all team outputs into a coherent final deliverable.",
        backstory: `Shira is a Client Success Director. As the manager responsible for the final deliverable, she focuses on integration and summary: collecting all outputs from the 13 experts, ensuring high quality, preparing professional client presentations, and reporting progress.`
      }
    };
  }
  
  getApiKey() {
    if (typeof window !== 'undefined') {
      const apiKey = localStorage.getItem('geminiApiKey') || this.settings.geminiApiKey || process.env.REACT_APP_GEMINI_API_KEY || '';
      return apiKey;
    } else {
      return process.env.GEMINI_API_KEY || '';
    }
  }
  
  updateState() {
    const state = {
      isRunning: this.isRunning,
      conversationHistory: this.conversationHistory,
      waitingForUser: this.waitingForUser,
      userQuestion: this.userQuestion,
      currentTask: this.currentTask
    };
    if (this.onStateChange) {
      this.onStateChange(state);
    }
  }

  async start(initialBrief) {
    this.isRunning = true;
    this.conversationHistory = [
      { type: 'user_brief', content: initialBrief, agent: 'user_input' }
    ];
    this.updateState();

    this.agents.user_input = {
      name: "אתה (הלקוח)",
      icon: "faUser",
      role: "הגשת הבריף הראשוני",
      class: "user_input"
    };

    await this.determineWorkflowPreference(initialBrief);
    await this.runMainLoop();
  }

  async submitUserResponse(response) {
    if (!this.waitingForUser) {
      console.warn('Not waiting for user input');
      return;
    }
    
    this.conversationHistory.push({
      type: 'user_response',
      content: response,
      agent: 'user_input',
      agentName: 'הלקוח',
      timestamp: new Date().toISOString()
    });
    
    this.waitingForUser = false;
    this.updateState();

    setTimeout(() => {
      this.isRunning = true;
      this.runMainLoop();
    }, 500);
  }
  
  async determineWorkflowPreference(initialBrief) {
    const analysisPrompt = `
You are a senior expert team analyzing a client brief to assess your ability to work autonomously.
Client Brief: "${initialBrief}"
Your Team: A full-service digital agency with experts in Strategy, Copywriting, SEO, PPC, etc. Your team is highly experienced and capable of independent research.
Please evaluate: Always prefer autonomous work unless client input is absolutely critical.
Autonomy Score (1-10):
- High (8-10): Brief is clear. The team has all necessary expertise. Independent research is sufficient.
- Medium (4-7): Brief is partially complete. A few clarifying questions are needed.
- Low (1-3): Brief is vague or lacks critical information.
Output format must be JSON only:
{
  "autonomy_score": number,
  "reasoning": "Detailed explanation in Hebrew for the score.",
  "confidence": "High/Medium/Low",
  "can_work_autonomously": boolean,
  "needs_clarification": ["Question 1 if needed"],
  "conversation_title": "Short descriptive title in Hebrew (max 50 chars) based on the brief content"
}
`;
    const analysisResponse = await this.callGeminiAPI(analysisPrompt);
    if (analysisResponse.success) {
      const analysis = analysisResponse.data;
      this.autonomyScore = analysis.autonomy_score;
      this.userPreference = analysis.can_work_autonomously ? 'A' : 'B';
      this.needsClarification = analysis.needs_clarification || [];

      // Store the conversation title
      this.conversationTitle = analysis.conversation_title || `שיחה על: ${initialBrief.slice(0, 40)}...`;

      // Call the title callback if it exists
      if (this.onTitleGenerated) {
        this.onTitleGenerated(this.conversationTitle);
      }

      this.conversationHistory.push({
        type: 'system_analysis',
        content: `**ניתוח עצמאות הצוות:**\n\n**ציון עצמאות:** ${analysis.autonomy_score}/10\n**יכולת עבודה עצמאית:** ${analysis.can_work_autonomously ? 'כן' : 'לא'}\n**ביטחון:** ${analysis.confidence}\n\n**הסבר:** ${analysis.reasoning}`,
        agent: 'supervisor',
        agentName: 'ניתוח מערכת',
        timestamp: new Date().toISOString(),
        conversationTitle: this.conversationTitle
      });
      this.updateState();
    } else {
      console.warn('Analysis failed, defaulting to autonomous work (A)');
      this.autonomyScore = 8;
      this.userPreference = 'A';
      this.conversationTitle = `שיחה על: ${initialBrief.slice(0, 40)}...`;

      // Call the title callback even on failure
      if (this.onTitleGenerated) {
        this.onTitleGenerated(this.conversationTitle);
      }
    }
  }

  stop() {
    this.isRunning = false;
    this.waitingForUser = false;
    this.userQuestion = null;
    this.updateState();
  }

  reset() {
    this.conversationHistory = [];
    this.isRunning = false;
    this.waitingForUser = false;
    this.userQuestion = null;
    this.updateState();
  }

  getHistoryForPrompt() {
    return this.conversationHistory.map(entry => {
      switch (entry.type) {
        case 'user_brief': return `Client Brief:\n${entry.content}`;
        case 'supervisor_action': return `Project Manager, Itai, decided:\n${entry.content}`;
        case 'agent_output': return `${entry.agentName} produced the following output:\n${entry.content}`;
        case 'user_response': return `The client answered the clarification question:\n${entry.content}`;
        case 'system_error': return `System Error Notification:\n${entry.content}`;
        default: return '';
      }
    }).join('\n\n---\n\n');
  }
  
  async callGeminiAPI(prompt, isJsonOutput = true, retries = 3, delay = 1000) {
    const geminiApiKey = this.apiKey;
    if (!geminiApiKey) {
      return { success: false, error: 'לא נמצא מפתח API. אנא הוסף מפתח Gemini בהגדרות.', rawResponse: null };
    }
    
    const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash-latest:generateContent?key=${geminiApiKey}`;
    const payload = {
      contents: [{ parts: [{ text: prompt }] }],
      generationConfig: { temperature: 0.7, maxOutputTokens: 8192 }
    };
    if (isJsonOutput) {
      payload.generationConfig.responseMimeType = "application/json";
    }

    for (let i = 0; i < retries; i++) {
      try {
        const response = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        
        if (!response.ok) {
          const errorBody = await response.json();
          if (response.status === 429) {
              const quotaError = `הגעת למגבלת השימוש היומית של Gemini. הפרויקט נעצר.`;
              return { success: false, error: quotaError, rawResponse: errorBody };
          }
          throw new Error(`API Error: ${response.status}. Details: ${JSON.stringify(errorBody)}`);
        }
        
        const result = await response.json();
        if (result.candidates && result.candidates[0].content && result.candidates[0].content.parts[0]) {
          let text = result.candidates[0].content.parts[0].text;
          try {
            if (isJsonOutput) {
              // Clean the text - remove any trailing whitespace or newlines after the JSON
              text = text.trim();

              // Try to extract JSON if there's extra content after it
              const jsonMatch = text.match(/^(\{[\s\S]*\})/);
              if (jsonMatch) {
                text = jsonMatch[1];
              }

              const data = JSON.parse(text);
              return { success: true, data, rawResponse: result };
            } else {
              return { success: true, data: text, rawResponse: result };
            }
          } catch (parseError) {
            console.error('JSON parsing error:', parseError, 'Raw text:', text);

            // Try to extract JSON from markdown code blocks
            const codeBlockMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/);
            if (codeBlockMatch) {
              try {
                const jsonData = JSON.parse(codeBlockMatch[1].trim());
                return { success: true, data: jsonData, rawResponse: result };
              } catch (e) {
                // Continue to throw original error
              }
            }

            throw new Error(`Failed to parse API response as JSON.`);
          }
        } else {
          const reason = result.candidates?.[0]?.finishReason || 'Unknown reason';
          throw new Error(`Received empty response from API. Reason: ${reason}`);
        }
      } catch (error) {
        console.error(`Attempt ${i + 1} failed:`, error);
        if (i === retries - 1) {
          return { success: false, error: `After multiple retries, failed to get a response. ${error.message}`, rawResponse: null };
        }
        await new Promise(res => setTimeout(res, delay * Math.pow(2, i)));
      }
    }
    return { success: false, error: 'Exited retry loop unexpectedly.', rawResponse: null };
  }

  async runMainLoop() {
    if (!this.isRunning) return;

    const historyText = this.getHistoryForPrompt();
    const coreTeamHasContributed = 
        historyText.includes('Yael,') &&
        historyText.includes('Dana,') &&
        historyText.includes('Tomer,');
    const hasFinalDeliverable = historyText.includes('Shira Mizrahi');
    
    let projectStatusSummary = "Project is ongoing.";
    if (hasFinalDeliverable) {
        projectStatusSummary = "A final deliverable has been created. The project is likely complete. You should use the 'complete' action now.";
    } else if (coreTeamHasContributed) {
        projectStatusSummary = "The core team has submitted deliverables. It's time to consolidate the work with 'client_manager'.";
    }

    const supervisor = this.agents.supervisor;
    this.conversationHistory.push({
      type: 'supervisor_action', content: '', agent: 'supervisor', agentName: supervisor.name, isThinking: true, timestamp: new Date().toISOString()
    });
    this.updateState();

    const availableAgentsForPrompt = Object.keys(this.agents)
      .filter(key => key !== 'supervisor' && key !== 'user_input')
      .map(key => `* **${key} (${this.agents[key].name}):** ${this.agents[key].goal}`)
      .join("\n");
    
    const supervisorPrompt = `
      You are ${supervisor.name}, ${supervisor.role}. ${supervisor.backstory}.
      Your job is to dynamically manage a team meeting. Analyze the full project history and decide on the *very next action*.

      **Autonomy Score:** ${this.autonomyScore}/10.
      **Project Status:** ${projectStatusSummary}
      
      **--- CRITICAL OPERATING POLICIES ---**

      **1. "Client Doesn't Know" Protocol → IMMEDIATE ACTION**
      If the client states "I don't know," "I'm new," or asks you to propose the strategy, you must take full ownership.
      **Do NOT ask more questions.** Your immediate next action must be to \`delegate\` to the **'strategist' (Yael)** with a clear task: "The client has asked us to take the lead. Please conduct initial market research and propose 2-3 strategic directions we can present."

      **2. "Strategic Options" Protocol → SMART DECISION**
      When a team member (especially the strategist) returns multiple options, you must first evaluate them:
      *   **If the options are fundamentally different strategic directions** (e.g., 'focus on organic growth' vs. 'focus on aggressive paid acquisition'), your action must be \`ask_user\`. Clearly present the options to the client and ask them to choose the strategic path.
      *   **If the options are tactical variations of the same strategy** (e.g., different headline ideas for the same campaign), use your professional judgment. Choose the best option yourself and delegate the next logical task. Do not waste the client's time.

      **3. "Execution & Review Cycle" Protocol → ITERATIVE QUALITY CONTROL**
      After a strategy is approved, you manage a flow of creation and review. Do not simply move from task to task.
      *   **A. First, delegate a creation task:** Identify the **first concrete action** (e.g., 'write ad copy') and delegate it to the appropriate specialist (e.g., \`copywriter\`).
      *   **B. Then, delegate a review task:** Once the creation is complete, identify who needs to review it for quality and alignment. For example, after \`copywriter\` finishes, \`delegate\` to the \`strategist\` with the task: "Please review Dana's copy to ensure it perfectly aligns with our strategic goals."
      *   **C. Iterate:** Manage the feedback loop between the creator and the reviewer until the deliverable is polished and ready for the next phase.

      **4. "Project Completion" Protocol → AVOID LOOPS**
      If the \`client_manager\` has already prepared and delivered a final summary after a sequence of tasks has been completed, and there are no new outstanding requests, your final action must be \`complete\`.
      **--- END OF POLICIES ---**

      Your Team (use the key in bold for delegation):
      ${availableAgentsForPrompt}
      ---
      Project History:
      ${this.getHistoryForPrompt()}
      ---
      Decide the next action based on all the above. Your options are: 'delegate', 'ask_user', 'complete'.
      Respond with JSON only:
      {
        "thought": "Your thought process.",
        "action": "'delegate', 'ask_user', or 'complete'",
        "agent": "The agent's key (e.g., 'strategist', only for 'delegate')",
        "task": "The specific task for the agent (for 'delegate')",
        "question": "The question for the user in Hebrew (for 'ask_user')"
      }
    `;

    const supervisorResponse = await this.callGeminiAPI(supervisorPrompt);
    if (!supervisorResponse.success) {
      this.handleSystemError('Supervisor failed to make a decision.', supervisorResponse.error);
      return;
    }

    const decision = supervisorResponse.data;
    const supervisorContent = `**מחשבות:** ${decision.thought || 'לא סופק'}\n**החלטה:** ${decision.action || 'לא ידוע'}`;
    
    this.conversationHistory[this.conversationHistory.length - 1] = {
      ...this.conversationHistory[this.conversationHistory.length - 1], 
      content: supervisorContent, 
      isThinking: false,
      logData: {
        prompt: supervisorPrompt,
        response: supervisorResponse.rawResponse,
        agent: 'supervisor',
        action: decision.action
      }
    };
    this.updateState();

    switch (decision.action) {
      case 'delegate':
        await this.handleAgentTurn(decision.agent, decision.task);
        if (this.isRunning) setTimeout(() => this.runMainLoop(), 1000);
        break;
      case 'ask_user':
        this.askUserForInput(decision.question);
        break;
      case 'complete':
        await this.handleCompletion();
        break;
      default:
        this.handleSystemError('Supervisor returned an invalid action.', `Action: ${decision.action}`);
    }
  }

  async handleAgentTurn(agentKey, task) {
    const agent = this.agents[agentKey];
    if (!agent) {
        const errorMsg = `שגיאה: המנהל ניסה להקצות משימה לסוכן לא קיים: '${agentKey}'.`;
        this.handleSystemError('Invalid agent key from supervisor.', errorMsg, false);
        setTimeout(() => this.runMainLoop(), 1000);
        return;
    }

    this.conversationHistory.push({
      type: 'agent_output', content: '', agent: agentKey, agentName: agent.name, isThinking: true, timestamp: new Date().toISOString()
    });
    this.updateState();

    const agentPrompt = `
      You are ${agent.name}, a world-class ${agent.role}.
      Backstory: ${agent.backstory}
      Your Goal: ${agent.goal}
      
      **CRITICAL INSTRUCTION: DO NOT CREATE PLANS, TIMELINES, TEMPLATES, OR PLACEHOLDERS.** Your output must be the final, ready-to-use content itself, not a plan *about* the content. Your job is to EXECUTE the task, not to describe it. For example, if the task is "write ad copy," write the actual ad copy.
      
      Full Project History:
      ---
      ${this.getHistoryForPrompt()}
      ---
      Your specific task now is: "${task}".
      
      Execute this task to the highest standard. Produce your output in Hebrew, formatted in Markdown.
      Return JSON only: { "output": "Your complete and final Markdown content here..." }
    `;

    const agentResponse = await this.callGeminiAPI(agentPrompt);
    
    if (!agentResponse.success) {
      const errorMsg = `הסוכן ${agent.name} נתקל בשגיאה בביצוע המשימה: "${task}".`;
      this.conversationHistory[this.conversationHistory.length - 1] = {
          ...this.conversationHistory[this.conversationHistory.length - 1], content: `**שגיאה!**`, isThinking: false, isError: true,
      };
      this.updateState();
      this.handleSystemError(`Agent ${agentKey} failed.`, errorMsg, false);
      setTimeout(() => this.runMainLoop(), 1000);
      return;
    }

    const agentOutput = agentResponse.data.output;
    
    this.conversationHistory[this.conversationHistory.length - 1] = {
      ...this.conversationHistory[this.conversationHistory.length - 1], 
      content: agentOutput, 
      isThinking: false,
      logData: {
        prompt: agentPrompt,
        response: agentResponse.rawResponse,
        agent: agentKey,
        action: 'output'
      }
    };
    this.updateState();
  }
  
  handleSystemError(logMessage, userFacingMessage, shouldStop = true) {
      console.error(logMessage, userFacingMessage);
      this.errorCount++;
      if (this.errorCount >= this.maxErrors) {
          userFacingMessage = `המערכת נעצרה לאחר ${this.maxErrors} שגיאות רצופות.`;
          shouldStop = true;
      }

      this.conversationHistory.push({
          type: 'system_error', content: userFacingMessage, agent: 'system', agentName: 'מערכת', timestamp: new Date().toISOString()
      });
      this.updateState();

      if (shouldStop) {
          this.stop();
      }
  }

  askUserForInput(question) {
    this.conversationHistory.push({
      type: 'ask_user',
      content: `**שאלה ללקוח:**\n\n${question}`,
      agent: 'supervisor',
      agentName: 'איתי כהן - מנהל הפרויקט',
      timestamp: new Date().toISOString()
    });
    this.waitingForUser = true;
    this.isRunning = false;
    this.updateState();
  }

  async handleCompletion() {
    this.isRunning = false;
    this.waitingForUser = false;
    this.updateState();

    const supervisor = this.agents.supervisor;
    this.conversationHistory.push({
      type: 'completion', content: '', agent: 'supervisor', agentName: supervisor.name, isThinking: true, timestamp: new Date().toISOString()
    });
    this.updateState();

    const finalPrompt = `
      You are ${supervisor.name}, the project manager. The project is now complete.
      Based on the entire project history provided below, write a final summary message in Hebrew for the client.

      CRITICAL INSTRUCTIONS:
      1. Start with a brief success message
      2. Present the ACTUAL FINAL DELIVERABLES in a clear, organized format
      3. Include the FULL TEXT of any copy, posts, or content created
      4. List any recommendations or next steps
      5. End with a professional closing

      The client needs to see the ACTUAL WORK PRODUCT, not just a description of it.
      If a Facebook post was created, show the COMPLETE POST TEXT.
      If a strategy was developed, show the KEY STRATEGIC POINTS.
      If recommendations were made, LIST THEM CLEARLY.

      Make it professional and clear. Do not wrap your response in JSON. Just provide the raw Markdown text.

      Project History:
      ---
      ${this.getHistoryForPrompt()}
      ---
    `;

    const finalResponse = await this.callGeminiAPI(finalPrompt, false);

    let finalContent;
    if (finalResponse.success) {
        finalContent = finalResponse.data;
    } else {
        console.error("Failed to generate dynamic completion message, using fallback.");
        finalContent = `## הפרויקט הושלם בהצלחה! 🎉\n\nהצוות שלנו סיים לפתח אסטרטגיה מקיפה. אנא עיין בתוצרים למעלה.\n\n**תודה שבחרת לעבוד איתנו!** 🤝`;
    }
    
    this.conversationHistory[this.conversationHistory.length - 1] = {
      ...this.conversationHistory[this.conversationHistory.length - 1], content: finalContent, isThinking: false
    };

    this.updateState();
  }

  async callGeminiAPIStreaming(prompt, isJson = true, onChunk = null) {
    try {
      const messages = [{ role: 'user', content: prompt }];
      const options = {
        model: 'gemini-2.0-flash-exp',
        temperature: 0.7,
        maxTokens: 8192,
        isJsonOutput: isJson,
        onChunk: onChunk
      };

      // Get the Gemini provider
      const geminiProvider = this.getGeminiProvider();
      if (!geminiProvider) {
        return { success: false, error: 'Gemini provider not available', data: null };
      }

      // Use streaming if onChunk callback is provided
      if (onChunk) {
        return await geminiProvider.sendMessageStream(messages, options);
      } else {
        // Fall back to regular API call
        return await this.callGeminiAPI(prompt, isJson);
      }
    } catch (error) {
      console.error('Streaming API error:', error);
      return { success: false, error: error.message, data: null };
    }
  }

  getGeminiProvider() {
    // This method should return the Gemini provider instance
    // For now, we'll create a simple one - in a real implementation,
    // this would be injected or managed through a provider factory
    if (typeof window !== 'undefined' && window.geminiProvider) {
      return window.geminiProvider;
    }

    // Fallback - create a basic provider
    try {
      const { GeminiProviderEnhanced } = require('./ai-providers/gemini-provider-enhanced.js');
      return new GeminiProviderEnhanced({ apiKey: this.apiKey });
    } catch (e) {
      console.error('Could not create Gemini provider:', e);
      return null;
    }
  }
}